/* eslint-disable @typescript-eslint/member-ordering */
import { LocationStrategy } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SidepanelService } from 'mbs-ui-kit';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { Administrator, OAuthCredential } from 'mbs-ui-shared@models';
import { BaseAuthCredentials } from 'mbs-ui-shared@models/auth-models';
import { DataForLoginUser } from 'mbs-ui-shared@models/login';
import { LoginService } from 'mbs-ui-shared@services/login.service';
import { LOGOUT_EVENT } from 'mbs-ui-shared@tokens/logout.token';
import { Observable, Subject, switchMap, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as AuthStoreActions from '../store/auth/actions';

const NOT_IMPLEMENTED = 'Method not implemented.';

@Injectable({
  providedIn: 'root'
})
export class LoginFacade {
  protected auth = inject(AuthFacade);
  protected store = inject(Store);
  protected router = inject(Router);
  protected sidepanelService = inject(SidepanelService);
  protected loginService = inject(LoginService);
  protected route = inject(ActivatedRoute);
  protected locationStrategy = inject(LocationStrategy);
  logoutEvent = inject<Subject<boolean>>(LOGOUT_EVENT);

  public loginByCredentials(credentials: BaseAuthCredentials): Observable<Administrator> {
    throw new Error(NOT_IMPLEMENTED);
  }

  public loginUserByCredentials(credentials: BaseAuthCredentials, data: DataForLoginUser): Observable<OAuthCredential> {
    throw new Error(NOT_IMPLEMENTED);
  }

  public handleLogin(authRequest: Observable<OAuthCredential>): Observable<Administrator> {
    return authRequest.pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
      tap((credentials) => {
        this.auth.credentials = credentials;
      }),
      switchMap(() => this.auth.fetchCurrentUser$)
    );
  }

  public handleOneTimeSessionLogin(authRequest: Observable<OAuthCredential>): Observable<Administrator> {
    return authRequest.pipe(
      tap((credentials) => {
        this.auth.oneTimeSessionCredentials = credentials;
      }),
      switchMap(() => this.auth.fetchCurrentUser$)
    );
  }

  public logout(path: string = RoutingPath.Login, ref?: string) {
    this.logoutEvent.next(true);
    this.clear();

    this.getLogoutRequest().subscribe(() => this.redirectToLogin(path, ref));
  }

  public redirectToLogin(path: string = RoutingPath.Login, ref?: string) {
    if (this.router.url.includes(path)) {
      return;
    }
    location.href = `${path}?logoff=1&ref=${encodeURIComponent(ref || this.router.url)}`;
  }

  public redirectToApp() {
    throw Error('Method not implemented');
  }

  public redirectToLoginWithState(path: string = RoutingPath.Login, ref?: string) {
    if (this.router.url.includes(path)) {
      return;
    }

    this.router
      .navigate([path], {
        queryParams: {
          logoff: 1,
          ref: ref ? encodeURIComponent(ref) : null
        }
      })
      .then();
  }

  public getLogoutRequest(): Observable<unknown> {
    throw new Error('Method not implemented.');
  }

  protected clear() {
    this.auth.endOneTimeSession();
    this.auth.clearSessionData();
    this.store.dispatch(AuthStoreActions.logout());
    this.sidepanelService.closeAll();
  }
}
